import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Trash2, Send, FileText, Inbox, Bell } from "lucide-react";
import { DashTabs, useTabParam } from "@/components/DashTabs";
import { LeadsBoard } from "@/components/collector/LeadsBoard";
import { NotifyPrefsCard } from "@/components/collector/NotifyPrefsCard";
import { CollectorProfileForm } from "@/components/collector/CollectorProfileForm";
import { CollectorChips, collectorName, useCollectorActions } from "@/components/company/CompanyTeamCard";
import { CopyLink } from "@/components/company/AddCollectorDialog";
import { ProfileCompletionCard } from "@/components/collector/ProfileCompletionCard";
import { CompanyLogo } from "@/components/TeamBadge";

const TAB_KEYS = ["profile", "leads", "alerts"];

const InviteStatus = ({ c, onResend }) => {
  if (c.has_login) return <p className="text-sm text-[#5C6B64]" data-testid="collector-invite-status">{collectorName(c)} has their own login and can also manage this profile and their leads.</p>;
  return (
    <div className="space-y-3" data-testid="collector-invite-status">
      <p className="text-sm text-[#5C6B64]">{c.invite?.status === "declined" ? "They declined the invite." : "They haven't created their login yet."} You can complete their profile yourself, or share the invite so they can finish it themselves.</p>
      {c.invite?.link && <CopyLink link={c.invite.link} testid="collector-invite-link" />}
      <button onClick={onResend} data-testid="collector-resend-invite" className="btn-secondary text-xs py-2 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5"/> Resend invite</button>
    </div>
  );
};

export default function CompanyCollector() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useTabParam(TAB_KEYS, "profile");
  const base = `/company/collectors/${id}`;

  const load = async () => {
    const [{ data: p }, { data: l }] = await Promise.all([api.get(base), api.get("/company/leads", { params: { collector_id: id } })]);
    setProfile(p);
    setLeads(l);
  };
  const actions = useCollectorActions(load);
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;
  const tabs = [
    { key: "profile", label: "Profile & Certifications", icon: FileText, count: (profile.certifications || []).length },
    { key: "leads", label: "Leads", icon: Inbox, count: leads.filter((l) => l.status === "requested").length, alert: true },
    { key: "alerts", label: "Alerts & Login", icon: Bell },
  ];

  return (
    <div className="container-x py-12" data-testid="company-collector-page">
      <Link to="/dashboard/company?tab=collectors" data-testid="company-collector-back" className="text-sm text-[#5C6B64] hover:text-[#1E3F33] inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4"/> Back to collectors</Link>
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2 inline-flex items-center gap-2">{profile.company_logo_url && <CompanyLogo logo={profile.company_logo_url} name={profile.company_name} size="w-6 h-6" testid="company-collector-logo" />}{profile.company_name} · Collector</div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold" data-testid="company-collector-name">{collectorName(profile)}</h1>
          <div className="mt-2"><CollectorChips c={profile} /></div>
        </div>
        <button onClick={() => actions.remove(profile, () => navigate("/dashboard/company?tab=collectors"))} data-testid="company-collector-remove" className="text-sm text-[#8A9A86] hover:text-[#E63946] inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4"/> Remove from company</button>
      </div>
      <ProfileCompletionCard completion={profile.profile_completion} nudges={profile.profile_nudges} onNudge={() => actions.nudge(profile)} onFix={tab !== "profile" ? () => setTab("profile") : null} />
      <DashTabs tabs={tabs} value={tab} onChange={setTab} testid="collector-tabs" />

      {tab === "profile" && <CollectorProfileForm profile={profile} setProfile={setProfile} base={base} reload={load} title="Collector profile" />}

      {tab === "leads" && (
        <LeadsBoard leads={leads} onChanged={load} canReassign title={`${collectorName(profile)}'s leads`} subtitle="Contact the customer, then confirm contact — or release the lead if it can't be completed." />
      )}

      {tab === "alerts" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <NotifyPrefsCard
              prefs={profile.notify_prefs} email={profile.email} phone={profile.phone} endpoint={`${base}/notify-prefs`}
              onSaved={(d) => setProfile((p) => ({ ...p, ...d }))} title="Collector's lead alerts"
              desc="How this collector is notified about new leads (sent to their own email/phone). Your company account is alerted separately."
            />
          </div>
          <div className="lg:col-span-5">
            <div className="card-soft" data-testid="collector-login-card">
              <h2 className="font-heading text-lg text-[#1E3F33] mb-2">Collector login</h2>
              <InviteStatus c={profile} onResend={() => actions.resend(profile)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
